import { isProduction } from 'config';
import { BankAccountTypes } from 'types/banks.types';
import {
  StandingOrderFrequency,
  type Connector,
} from 'types/transactions.types';
import { AvailableCountries, type IUser } from 'types/users.types';
import { isTaxOfficeIBAN, isTaxOfficeName } from 'utils/helpers';
import { TransferTypes } from './MoneyTransfer/types';

export const isUserConnectedToWallet = (connectors: Connector[] = []) =>
  getMainAccountableBank(connectors) &&
  getTaxSavingsAccountableBank(connectors);

export const isAccountableBank = (connector: Partial<Connector>) =>
  connector?.provider === 'swan';

export const isCustomSubAccount = (connector: Partial<Connector>) =>
  connector?.isCustomSubAccount;

export const isMainAccountableBank = (
  connector: Partial<Connector> | undefined,
) =>
  connector?.provider === 'swan' &&
  connector?.bankAccountReference?.name === BankAccountTypes.mainAccount;

export const getMainAccountableBank = (connectors: Connector[]) =>
  connectors?.find(
    (connector) =>
      connector?.provider === 'swan' &&
      connector?.bankAccountReference?.name === BankAccountTypes.mainAccount,
  );

export const isTaxReserveAccountableBank = (
  connector: Partial<Connector> | undefined,
): boolean =>
  connector?.provider === 'swan' &&
  connector?.bankAccountReference?.name === BankAccountTypes.taxReserveAccount;

export const getTaxSavingsAccountableBank = (connectors: Connector[]) =>
  connectors?.find(
    (connector) =>
      connector?.provider === 'swan' &&
      connector?.bankAccountReference?.name ===
        BankAccountTypes.taxReserveAccount,
  );

export const isAccountableBankingReady = (connector: Connector) =>
  connector?.provider === 'swan' && connector?.status === 'ready';

export const isAccountableBankingRefused = (connector: Connector) =>
  connector?.provider === 'swan' && connector?.status === 'refused';

export const isAccountableBankingAuthorizationFailure = (
  connector: Connector,
) =>
  connector?.provider === 'swan' && connector?.status === 'authorizationFailed';

export const canAccessAccountableBanking = (user?: IUser) => {
  if (!isProduction) return true;

  // company users are not allowed to see the Accountable Banking beta, however, there are some exceptions for internal users
  const isCompany =
    user?.accountType === 'one_person_company' &&
    !user?.featureFlags?.includes('Accountable-Banking-Allow-Company');

  return (
    !isCompany &&
    (user?.country === AvailableCountries.germany ||
      user?.country === AvailableCountries.belgium)
  );
};

export const isTaxOfficeTransfer = (counterpartyName: string, iban: string) =>
  isTaxOfficeName(counterpartyName) && isTaxOfficeIBAN(iban);

export const getTransferType = (
  isInstantTransfer: boolean,
  frequencyValue: StandingOrderFrequency,
) => {
  if (frequencyValue !== StandingOrderFrequency.OneTime) {
    if (isInstantTransfer) return TransferTypes.instant_standing_order;

    return TransferTypes.regular_standing_order;
  }

  if (isInstantTransfer) return TransferTypes.instant_sepa;

  return TransferTypes.sepa;
};

/**
 * Find a connector by its ID
 */
const findConnectorById = (
  connectors: Connector[],
  optionValue: string,
): Connector | undefined => {
  return connectors.find((connector) => connector._id === optionValue);
};

/**
 * Create a reusable connector change handler
 */
export const createConnectorChangeHandler = (
  connectors: Connector[],
  setter: (connector: Connector | undefined) => void,
) => {
  return (option: any) => {
    const connector = findConnectorById(connectors, option.value);
    setter(connector);
  };
};
